import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RevenueDialog({ open, onClose, onSuccess, eventId, revenue = null }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    notes: '',
    date: new Date().toISOString().split('T')[0],
  });

  // Reset form when dialog opens or revenue changes
  useEffect(() => {
    if (revenue) {
      setFormData({
        amount: revenue.amount?.toString() || '',
        notes: revenue.notes || '',
        date: revenue.date || new Date().toISOString().split('T')[0],
      });
    } else {
      setFormData({
        amount: '',
        notes: '',
        date: new Date().toISOString().split('T')[0],
      });
    }
  }, [revenue, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };

      const payload = {
        event_id: eventId,
        amount: parseFloat(formData.amount),
        notes: formData.notes,
        date: formData.date,
      };

      if (revenue) {
        // Update existing revenue
        await axios.put(`${BACKEND_URL}/api/revenues/${revenue.id}`, null, {
          headers,
          params: payload
        });
        toast.success('Revenue updated successfully');
      } else {
        // Create new revenue
        await axios.post(`${BACKEND_URL}/api/revenues`, payload, { headers });
        toast.success('Revenue added successfully');
      }

      setFormData({
        amount: '',
        notes: '',
        date: new Date().toISOString().split('T')[0],
      });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save revenue');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">
            {revenue ? 'Edit Revenue' : 'Add Revenue'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="amount">Amount *</Label>
            <Input
              id="amount"
              name="amount"
              type="number"
              step="0.01"
              data-testid="revenue-amount-input"
              value={formData.amount}
              onChange={handleChange}
              required
              className="bg-secondary/50 border-transparent focus:border-primary"
            />
          </div>

          <div>
            <Label htmlFor="date">Date *</Label>
            <Input
              id="date"
              name="date"
              type="date"
              data-testid="revenue-date-input"
              value={formData.date}
              onChange={handleChange}
              required
              className="bg-secondary/50 border-transparent focus:border-primary"
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              name="notes"
              data-testid="revenue-notes-input"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="bg-secondary/50 border-transparent focus:border-primary"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="revenue-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="revenue-submit-button"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? 'Saving...' : revenue ? 'Update Revenue' : 'Add Revenue'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
